import React from 'react'
import { Grid } from '@mui/material'
import {
  SimpleForm,
  TextInput,
  required,
  DateTimeInput,
  useEditContext,
  SelectInput,
  BooleanInput,
} from 'react-admin'
import { isAfterDate, isBeforeDate } from '../../utils/validators'
import { COMMON_FIELDS } from '../../providers/localization/constant'
import { EditBase } from '../../components/base'
import dayjs from 'dayjs'
import { PromotionBannerImageInputs } from '../../components/domains/promotion/PromotionBannerImageInputs'
import { ApproveAndPublishToolBar, TextInputTargetValue } from '../../components/form'
import { PromotionTargetLinkSelectInput } from '../../components/domains/promotion/PromotionTargetLinkSelectInput'
import { PromotionSelectInput } from '../../components/domains/promotion/PromotionSelectInput'

const RESOURCE_PREFIX = 'resources.promotions.banners.edit'

const EditForm = (): JSX.Element => {
  const { isLoading, record } = useEditContext()
  if (isLoading) return null
  const { is_published: isPublished, target_link: targetLink, promotion_id: promotionId } = record
  const defaultDate = dayjs()
  const defaultExpireDate = defaultDate.add(1, 'month')
  const putDefaultValue = (): object => ({
    action: targetLink || promotionId ? 'link' : 'none',
    action_type: targetLink ? 'external_browser' : promotionId ? 'promotions' : '',
  })

  return (
    <SimpleForm
      toolbar={<ApproveAndPublishToolBar encType='multipart/form-data' />}
      defaultValues={putDefaultValue}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <PromotionBannerImageInputs isPublished={isPublished} record={record} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectInput
            source='action'
            choices={[
              { id: 'none', name: 'None' },
              { id: 'link', name: 'Link' },
            ]}
            sx={{ marginTop: 0 }}
            fullWidth
            disabled={isPublished}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PromotionTargetLinkSelectInput disabled={isPublished} />
        </Grid>
        <Grid item xs={4}>
          <PromotionSelectInput disabled={isPublished} size='small'></PromotionSelectInput>
          <TextInputTargetValue
            label={`${COMMON_FIELDS}.target_link.label`}
            source='target_link'
            disabled={isPublished}
          ></TextInputTargetValue>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateTimeInput
            disabled={isPublished}
            source='start_date'
            label={`${COMMON_FIELDS}.date.published_on`}
            fullWidth
            defaultValue={defaultDate}
            validate={[
              required(),
              isBeforeDate('end_date', 'The publish date must be in before the expired date.'),
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateTimeInput
            disabled={isPublished}
            source='end_date'
            label={`${COMMON_FIELDS}.date.expired_on`}
            fullWidth
            defaultValue={defaultExpireDate}
            validate={[
              required(),
              isAfterDate('start_date', 'The expired date must be in after the publish date.'),
            ]}
          />
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <BooleanInput
              source='is_highlight'
              label={`${COMMON_FIELDS}.highlight.label`}
              disabled={isPublished}
              sx={{ '& .MuiFormControlLabel-root': { marginRight: 'auto' }, display: 'none' }}
              defaultValue={true}
            />
          </Grid>
        </Grid>
        <TextInput
          disabled={isPublished}
          sx={{ display: 'none' }}
          source='order'
          label={`${COMMON_FIELDS}.order`}
          fullWidth
          defaultValue={0}
        />
      </Grid>
    </SimpleForm>
  )
}

const PromotionBannerEdit = (): JSX.Element => {
  return (
    <EditBase
      title={`${RESOURCE_PREFIX}.title`}
      actions={false}
      listAction={{
        hasShow: false,
        hasBackButton: true,
      }}
    >
      <EditForm />
    </EditBase>
  )
}

export default PromotionBannerEdit
